import { classifyEncounter, DIFFICULTY_BANDS, MINION_LEVEL_SHARE, MINIONS_PER_HERO_MAX, MONSTERS_PER_HERO, monsterBudget, type Difficulty } from './encounter-budget.js';
import { seededRandom } from './seeded-random.js';

/**
 * Procedural encounter composition. A request names a difficulty, a reference party, a theme (the roles a faction fields) and a seed;
 * the answer is a list of role ids whose cost lands in that difficulty's band against the party's Monster Budget. The generator is
 * pure and knows no map: the Ember Coast calls it once per site at load, and the Dungeon Master will call it for map events.
 *
 * The rules are a general pattern, not a template. Only the hard limits below are enforced; everything else is weighting, so two
 * sites of one tier rarely look alike.
 */
export type EncounterClass = 'melee' | 'minion' | 'shooter' | 'caster';

export interface EncounterRole {
  id: string;
  kind: 'minion' | 'monster';
  /** Monster level from the stat block; minions have none (they cost half a hero level). */
  level?: number;
  /** `shooter` fights from range; `caster` is a spellcaster; `minion` is a melee minion. */
  class: EncounterClass;
  /** Armor from the stat block; armor waits for heroes who can answer it. */
  armor: number;
  /** How often the theme fields this role next to its siblings. */
  weight: number;
  /** Most of this role one group may field (a pack has one leader). Absent means no limit. */
  maxPerGroup?: number;
}

export interface EncounterTheme { id: string; roles: readonly EncounterRole[] }

export interface CompositionRules {
  /** Shooters and casters together may be at most this share of a group. */
  maxShooterShare: number;
  /** Past this share a further shooter becomes unlikely rather than impossible. */
  preferredShooterShare: number;
  /** Casters allowed per group: `base`, or the override for a difficulty. */
  maxCasters: { base: number } & Partial<Record<Exclude<Difficulty, 'trivial'>, number>>;
  /** A caster is never fielded without at least one melee body or minion in front of it. */
  casterNeedsScreen: boolean;
  /** Fewest opponents per hero (two per four heroes). */
  minMembersPerHero: number;
  /** Most opponents per hero, so a swarm of cheap bodies stays a crew rather than an army. */
  maxMembersPerHero: number;
}

export const DEFAULT_COMPOSITION_RULES: CompositionRules = {
  maxShooterShare: .5, preferredShooterShare: 1 / 3, maxCasters: { base: 1, 'very-deadly': 2 }, casterNeedsScreen: true,
  minMembersPerHero: MONSTERS_PER_HERO.min, maxMembersPerHero: 2.5,
};

export interface EncounterRequest {
  /** Same seed and request, same composition. */
  seed: string | number;
  difficulty: Exclude<Difficulty, 'trivial'>;
  party: { heroes: number; level: number };
  theme: EncounterTheme;
  /** Roles the group must contain, such as a caster for a caster-led site. */
  include?: readonly string[];
  /** Compositions (see `compositionKey`) the result should not repeat, such as a neighbour's. Best effort. */
  avoid?: ReadonlySet<string>;
  rules?: Partial<CompositionRules>;
}

const ATTEMPTS = 400, EPSILON = 1e-9;

export const compositionKey = (members: readonly string[]): string => [...members].sort().join(' ');

export function roleCost(role: EncounterRole, heroLevel: number): number {
  if (role.kind === 'minion') return heroLevel * MINION_LEVEL_SHARE;
  if (role.level === undefined) throw new Error(`Role ${role.id} has no level.`);
  return role.level;
}

/** Armor from Medium needs level 3 heroes and Heavy level 8; roles heroes cannot yet answer are left out. */
function fieldable(role: EncounterRole, heroLevel: number): boolean {
  return role.armor < 10 || role.armor < 20 && heroLevel >= 3 || heroLevel >= 8;
}

const isRanged = (role: EncounterRole) => role.class === 'shooter' || role.class === 'caster';

/** Why a composition breaks the hard limits; empty when it is acceptable. Exported so tests and the Dungeon Master can check any roster. */
export function compositionViolations(members: readonly EncounterRole[], request: Pick<EncounterRequest, 'difficulty' | 'party' | 'rules'>): string[] {
  const rules = { ...DEFAULT_COMPOSITION_RULES, ...request.rules, maxCasters: { ...DEFAULT_COMPOSITION_RULES.maxCasters, ...request.rules?.maxCasters } };
  const { heroes, level } = request.party, problems: string[] = [];
  const minions = members.filter(role => role.kind === 'minion').length, casters = members.filter(role => role.class === 'caster').length;
  const ranged = members.filter(isRanged).length;
  if (members.length < Math.ceil(heroes * rules.minMembersPerHero)) problems.push(`only ${members.length} opponents`);
  if (members.length > heroes * rules.maxMembersPerHero) problems.push(`${members.length} opponents`);
  if (members.length - minions > heroes * MONSTERS_PER_HERO.max) problems.push('too many monsters');
  if (minions > heroes * MINIONS_PER_HERO_MAX) problems.push('too many minions');
  if (ranged > members.length * rules.maxShooterShare + EPSILON) problems.push(`${ranged} of ${members.length} are shooters`);
  if (casters > (rules.maxCasters[request.difficulty] ?? rules.maxCasters.base)) problems.push(`${casters} casters`);
  if (casters && rules.casterNeedsScreen && !members.some(role => !isRanged(role))) problems.push('casters without a screen');
  const classed = classifyEncounter(members.reduce((sum, role) => sum + roleCost(role, level), 0), monsterBudget(Array(heroes).fill(level)));
  if (classed !== request.difficulty) problems.push(`costs ${classed}, not ${request.difficulty}`);
  for (const role of new Set(members)) if (role.maxPerGroup !== undefined && members.filter(other => other.id === role.id).length > role.maxPerGroup) problems.push(`more than ${role.maxPerGroup} ${role.id}`);
  for (const role of members) if (!fieldable(role, level)) problems.push(`${role.id} is too armored for level ${level} heroes`);
  return problems;
}

/** The role ids of one encounter, in a shuffled order so formations do not group by type. */
export function generateEncounter(request: EncounterRequest): string[] {
  const rules = { ...DEFAULT_COMPOSITION_RULES, ...request.rules, maxCasters: { ...DEFAULT_COMPOSITION_RULES.maxCasters, ...request.rules?.maxCasters } };
  const { heroes, level } = request.party, budget = monsterBudget(Array(heroes).fill(level));
  const [floor, ceiling] = DIFFICULTY_BANDS[request.difficulty], random = seededRandom(`${request.theme.id}/${request.difficulty}/${request.seed}`);
  const pool = request.theme.roles.filter(role => fieldable(role, level) && role.weight > 0);
  const byId = new Map(request.theme.roles.map(role => [role.id, role]));
  const required = (request.include ?? []).map(id => byId.get(id) ?? (() => { throw new Error(`Theme ${request.theme.id} has no role ${id}.`); })());
  /** Another caster only joins while the group is under its caster limit. */
  const limitOf = (role: EncounterRole) => role.class === 'caster' ? rules.maxCasters[request.difficulty] ?? rules.maxCasters.base : Infinity;

  const attempt = (avoid: ReadonlySet<string> | undefined): EncounterRole[] | undefined => {
    const goal = (floor + random() * (ceiling - floor)) * budget;
    let members = [...required], cost = members.reduce((sum, role) => sum + roleCost(role, level), 0);
    while (cost < goal - EPSILON) {
      const candidates = pool.filter(role => members.length < heroes * rules.maxMembersPerHero && cost + roleCost(role, level) < ceiling * budget - EPSILON &&
        members.filter(other => other.class === 'caster').length < limitOf(role) && (role.maxPerGroup === undefined || members.filter(other => other.id === role.id).length < role.maxPerGroup));
      const weights = candidates.map(role => {
        if (!isRanged(role)) return role.weight;
        const share = (members.filter(isRanged).length + 1) / (members.length + 1);
        return share > rules.maxShooterShare ? 0 : role.weight * (share > rules.preferredShooterShare ? .5 : 1);
      });
      const total = weights.reduce((sum, weight) => sum + weight, 0);
      if (total <= 0) break;
      let roll = random() * total, index = 0;
      while (roll >= weights[index] && index < weights.length - 1) roll -= weights[index++];
      members = [...members, candidates[index]]; cost += roleCost(candidates[index], level);
    }
    if (compositionViolations(members, request).length) return undefined;
    return avoid?.has(compositionKey(members.map(role => role.id))) ? undefined : members;
  };

  for (const avoid of [request.avoid, undefined]) for (let i = 0; i < ATTEMPTS; i++) {
    const members = attempt(avoid);
    if (!members) continue;
    for (let n = members.length - 1; n > 0; n--) { const swap = Math.floor(random() * (n + 1)); [members[n], members[swap]] = [members[swap], members[n]]; }
    return members.map(role => role.id);
  }
  throw new Error(`No ${request.difficulty} encounter for theme ${request.theme.id} and ${heroes} level-${level} heroes.`);
}
